import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GENDERS_BY_NAME } from 'src/common/constants/genders';
import { getAgeGroupParticipantCount } from 'src/helpers/statisticExtras';
import { EAgeGroups, TStatistic } from 'src/interfaces/Statistics';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Legend, Tooltip);

interface Props {
  statistic: TStatistic;
}

export default function FieldSection({ statistic }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { checkinsByGenderByAgeKeys, dataByGender, dataByGenderByAge } = statistic;

  const genderDonut = useMemo(() => {
    const labels = checkinsByGenderByAgeKeys.map(g =>
      t(`base.gender.${GENDERS_BY_NAME[g]?.name ?? g}`),
    );
    const data = checkinsByGenderByAgeKeys.map(k => dataByGender[k]?.ratio ?? 0);
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: [theme.palette.primary.main, theme.palette.secondary.main],
        },
      ],
    };
  }, [checkinsByGenderByAgeKeys, dataByGender, t, theme.palette.primary.main, theme.palette.secondary.main]);

  const ageBar = useMemo(() => {
    const labels = Object.values(EAgeGroups).map(key => t('statistics.keyAgeGroup', { key }));
    const totals = Object.values(EAgeGroups).map(ageKey =>
      checkinsByGenderByAgeKeys.reduce(
        (sum, gender) =>
          sum + getAgeGroupParticipantCount(dataByGenderByAge[gender]?.[ageKey]),
        0,
      ),
    );
    return {
      labels,
      datasets: [
        {
          label: t('insights.field.participantsByAge'),
          data: totals,
          backgroundColor: theme.palette.primary.light,
        },
      ],
    };
  }, [checkinsByGenderByAgeKeys, dataByGenderByAge, t, theme.palette.primary.light]);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('statistics.fmRatio')}
        </Typography>
        <Box sx={{ height: 280, width: '100%' }}>
          <Doughnut
            data={genderDonut}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: {
                legend: { position: 'bottom' },
              },
            }}
          />
        </Box>
      </Grid>
      <Grid size={{ xs: 12, md: 8 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('insights.field.participantsByAge')}
        </Typography>
        <Box sx={{ height: 280, width: '100%' }}>
          <Bar
            data={ageBar}
            options={{
              indexAxis: 'y',
              maintainAspectRatio: false,
              responsive: true,
              plugins: { legend: { display: false } },
              scales: {
                x: { beginAtZero: true, ticks: { stepSize: 1 } },
              },
            }}
          />
        </Box>
      </Grid>
    </Grid>
  );
}
